import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { expensesApi } from '../../api/expensesApi';
import StorageUtil from '../../utils/storage';
import { friendlyLoginError, LOGIN_ERROR_MESSAGES } from '../../utils/authErrors';

const errorPayload = (error, fallback) => {
  const data = error.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.detail || data.message || data.error || fallback;
};

const syncState = (state, payload) => {
  state.date = payload.date ?? state.date;
  state.notes = payload.notes ?? state.notes;
  state.list = payload.expenses ?? state.list;
  state.totalAmount = payload.total_amount ?? state.totalAmount;
  state.count = payload.total_expenses ?? state.list.length;
};

export const loginExpenses = createAsyncThunk(
  'expenses/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await expensesApi.login(credentials);
      return response.data;
    } catch (error) {
      if (!error.response) return rejectWithValue(LOGIN_ERROR_MESSAGES.NETWORK);
      return rejectWithValue(friendlyLoginError(error.response.data));
    }
  }
);

export const logoutExpenses = createAsyncThunk(
  'expenses/logout',
  async () => {
    try { await expensesApi.logout(); } catch (_) {}
    StorageUtil.clearExpensesAuth();
  }
);

export const fetchTodayExpenses = createAsyncThunk(
  'expenses/fetchToday',
  async (_, { rejectWithValue }) => {
    try {
      const response = await expensesApi.getToday();
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to load expenses'));
    }
  }
);

export const addExpense = createAsyncThunk(
  'expenses/add',
  async (data, { rejectWithValue }) => {
    try {
      const response = await expensesApi.add(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to add expense'));
    }
  }
);

export const updateExpense = createAsyncThunk(
  'expenses/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await expensesApi.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to update expense'));
    }
  }
);

export const deleteExpense = createAsyncThunk(
  'expenses/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await expensesApi.remove(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to delete expense'));
    }
  }
);

const expensesSlice = createSlice({
  name: 'expenses',
  initialState: {
    // auth
    accessToken: StorageUtil.getExpensesAccessToken(),
    isAuthenticated: StorageUtil.isExpensesAuthenticated(),
    authLoading: false,
    authError: null,
    // today's data
    date: '',
    notes: '',
    list: [],
    totalAmount: '0.00',
    count: 0,
    // loading states
    loading: false,
    submitting: false,
    updating: false,
    deleting: false,
    // error states
    error: null,
    updateError: null,
    deleteError: null,
  },
  reducers: {
    clearExpensesError: (state) => {
      state.error = null;
      state.updateError = null;
      state.deleteError = null;
    },
    clearAuthError: (state) => { state.authError = null; },
  },
  extraReducers: (builder) => {
    builder
      // login
      .addCase(loginExpenses.pending, (state) => { state.authLoading = true; state.authError = null; })
      .addCase(loginExpenses.fulfilled, (state, { payload }) => {
        state.authLoading = false;
        state.accessToken = payload.access_token;
        state.isAuthenticated = true;
        StorageUtil.setExpensesAccessToken(payload.access_token);
        if (payload.refresh_token) StorageUtil.setExpensesRefreshToken(payload.refresh_token);
      })
      .addCase(loginExpenses.rejected, (state, { payload }) => { state.authLoading = false; state.authError = payload; })

      // logout
      .addCase(logoutExpenses.fulfilled, (state) => {
        state.accessToken = null;
        state.isAuthenticated = false;
        state.list = [];
        state.date = '';
        state.notes = '';
        state.totalAmount = '0.00';
        state.count = 0;
      })

      // fetch today
      .addCase(fetchTodayExpenses.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTodayExpenses.fulfilled, (state, { payload }) => { state.loading = false; syncState(state, payload); })
      .addCase(fetchTodayExpenses.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })

      // add
      .addCase(addExpense.pending, (state) => { state.submitting = true; state.error = null; })
      .addCase(addExpense.fulfilled, (state, { payload }) => {
        state.submitting = false;
        if (payload?.expenses || payload?.date) {
          syncState(state, payload);
        } else {
          const added = Array.isArray(payload) ? payload : [payload];
          state.list = [...added, ...state.list];
          state.count += added.length;
          const prev = parseFloat(state.totalAmount) || 0;
          const addedTotal = added.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
          state.totalAmount = (prev + addedTotal).toFixed(2);
        }
      })
      .addCase(addExpense.rejected, (state, { payload }) => { state.submitting = false; state.error = payload; })

      // update (PATCH — returns full day picture)
      .addCase(updateExpense.pending, (state) => { state.updating = true; state.updateError = null; })
      .addCase(updateExpense.fulfilled, (state, { payload }) => { state.updating = false; syncState(state, payload); })
      .addCase(updateExpense.rejected, (state, { payload }) => { state.updating = false; state.updateError = payload; })

      // delete (DELETE — returns full day picture)
      .addCase(deleteExpense.pending, (state) => { state.deleting = true; state.deleteError = null; })
      .addCase(deleteExpense.fulfilled, (state, { payload }) => { state.deleting = false; syncState(state, payload); })
      .addCase(deleteExpense.rejected, (state, { payload }) => { state.deleting = false; state.deleteError = payload; });
  },
});

export const { clearExpensesError, clearAuthError } = expensesSlice.actions;
export default expensesSlice.reducer;
